import { bootstrapApp } from '@k2/core'
import { createHead } from '@k2/core/head'
import { createUi } from '@k2/ui'
import type { Theme } from '@k2/ui/theme'
import App from './App.vue'
import { createApi } from './api'
import { createAssets } from './assets'
import { createAuth } from './auth'
import { createBrands } from './brands'
import { createCalendar } from './calendar'
import { createCampaigns } from './campaigns'
import { createChat } from './chat'
import { createDashboard } from './dashboard'
import { createDeliverables } from './deliverables'
import { messages } from './i18n'
import { createLayout } from './layout'
import { createMarkdown } from './markdown'
import { createMcp } from './mcp'
import { createPlatformInvitations } from './platform-invitations'
import { createSetup } from './setup'
import { createTeam } from './team'
import { createTemplates } from './templates'
import { createVerification } from './verification'
import './styles.css'

export type CreateAppOptions = { language?: string; url?: string | URL; theme?: Theme }

export const createApp = async ({ language, url, theme }: CreateAppOptions = {}) => {
  const app = await bootstrapApp(App, { messages, language, url })
  const router = app.getRouter()

  router.beforeEach(to => {
    const resolved = router.resolve(to)
    if (!to.matched.length && resolved.matched.length) {
      return resolved
    }
  })

  const i18n = app.getI18n()

  app
    .use(createUi({ theme, i18n }))
    .use(createHead())
    .use(createAuth())
    .use(createApi())
    .use(createMarkdown())
    .use(createChat())
    .use(createLayout())
    .use(createBrands())
    .use(createSetup())
    .use(createDashboard())
    .use(createCalendar())
    .use(createCampaigns())
    .use(createDeliverables())
    .use(createAssets())
    .use(createTemplates())
    .use(createTeam())
    .use(createMcp())
    .use(createVerification())
    .use(createPlatformInvitations())

  router.addRoute({ path: '/:catchAll(.*)', redirect: { name: 'home' } })
  return app
}
